import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { router, Head, usePage } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Archive,
  Check,
  RefreshCw,
  MinusCircle,
  Plug,
  Search,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  Loader2,
  MessageCircle,
} from 'lucide-react';
import { PROVIDER_ICONS_SMALL } from '@/frontend/components/DigitalWorkers/providerIcons';
import AssignWorkersModal from '@/frontend/components/Integrations/AssignWorkersModal';
import { ConnectionStatusChip } from '@/frontend/components/Integrations/ConnectionStatusChip';
import AssignedWorkersBanner from '@/frontend/components/Integrations/AssignedWorkersBanner';
import PostConnectionModal from '@/frontend/components/PostConnectionModal';
import { usePermissions } from '@/frontend/hooks/usePermissions';
import { useJustConnectedWorker } from '@/frontend/hooks/useJustConnectedWorker';
import DashboardLayout from '@/frontend/layouts/DashboardLayout';
import { BackLink } from '@/frontend/components/BackLink';
import type { AdAccount, AvailableAdAccount, AssignWorker } from './types';

interface WhatsappWaba {
  id: string;
  name: string | null;
  external_id: string;
  webhook_subscribed: boolean;
  phones_count: number;
}

// Cabeçalho de tabela canônico do portal (design.md: th 11/650 uppercase baixa).
const TH_CLS = 'text-[11px] font-semibold uppercase tracking-wide text-faint-foreground';

interface Props {
  page_title: string;
  meta_connected: boolean;
  whatsapp_connected: boolean;
  whatsapp_waba: WhatsappWaba | null;
  available_ad_accounts: AvailableAdAccount[];
  user_ad_accounts: AdAccount[];
  digital_workers: AssignWorker[];
}

const MetaPage = ({
  page_title,
  meta_connected,
  whatsapp_connected,
  whatsapp_waba,
  available_ad_accounts = [],
  user_ad_accounts = [],
  digital_workers = [],
}: Props) => {
  const { can_manage_resources } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [metaAccounts, setMetaAccounts] = useState<AvailableAdAccount[]>(available_ad_accounts);
  const [userAccounts, setUserAccounts] = useState<AdAccount[]>(user_ad_accounts);
  const [loadingById, setLoadingById] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showTopScrollIndicator, setShowTopScrollIndicator] = useState(false);
  const [showBottomScrollIndicator, setShowBottomScrollIndicator] = useState(false);
  const [adConnectedSearchTerm, setAdConnectedSearchTerm] = useState('');
  const { flash } = usePage<{ flash: { assign_workers?: boolean } }>().props;
  const [assignWorkersOpen, setAssignWorkersOpen] = useState(
    () => !!flash?.assign_workers && digital_workers.length > 0
  );
  const [savingWorkerAssignment, setSavingWorkerAssignment] = useState(false);
  const justConnected = useJustConnectedWorker(digital_workers);

  useLayoutEffect(() => {
    if (dialogOpen && searchTerm !== '') {
      requestAnimationFrame(() => {
        const input = document.querySelector('[role="dialog"] input:not([type="checkbox"])') as HTMLInputElement;
        if (input && document.activeElement !== input) {
          input.focus();
          input.setSelectionRange(input.value.length, input.value.length);
        }
      });
    }
  }, [dialogOpen, searchTerm]);

  useEffect(() => {
    if (user_ad_accounts) {
      setUserAccounts(user_ad_accounts);
    }
  }, [user_ad_accounts]);

  useEffect(() => {
    if (available_ad_accounts && available_ad_accounts.length > 0) {
      setMetaAccounts(available_ad_accounts);
    }
  }, [available_ad_accounts]);

  useEffect(() => {
    setTimeout(() => {
      const scrollContainer = document.getElementById('ad-accounts-scroll-container');
      if (scrollContainer) {
        const { scrollHeight, clientHeight } = scrollContainer;
        setShowBottomScrollIndicator(scrollHeight > clientHeight);
      }
    }, 0);
  }, [userAccounts]);

  const filteredAdAccounts = useMemo(() => {
    if (!adConnectedSearchTerm.trim()) return userAccounts;
    const term = adConnectedSearchTerm.toLowerCase();
    return userAccounts.filter(account =>
      account.name.toLowerCase().includes(term) ||
      account.external_id.toLowerCase().includes(term)
    );
  }, [userAccounts, adConnectedSearchTerm]);

  const fetchAvailableAccounts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/dashboard/ad_accounts/available');
      if (!response.ok) {
        if (response.status === 401) {
          alert('Você precisa conectar sua conta do Meta/Facebook primeiro.');
        }
        return;
      }
      const data = await response.json();
      if (data.ad_accounts) {
        const existingAccountIds = userAccounts.map(acc => acc.external_id);
        const availableAccounts = data.ad_accounts.map((account: AvailableAdAccount) => ({
          ...account,
          imported: existingAccountIds.includes(account.external_id)
        }));
        setMetaAccounts(availableAccounts);
      } else {
        setMetaAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching Meta accounts:', error);
      setMetaAccounts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAccountSelection = (accountId: string) => {
    setMetaAccounts(accounts =>
      accounts.map(account =>
        account.external_id === accountId
          ? { ...account, selected: !account.selected }
          : account
      )
    );
  };

  const convertAccountForSubmission = (account: AvailableAdAccount) => ({
    external_id: account.external_id,
    name: account.name,
    currency: account.currency,
    status: account.status
  });

  const importSelectedAccounts = async () => {
    const selectedAccounts = metaAccounts.filter(account => account.selected && !account.imported);
    if (selectedAccounts.length === 0) return;
    setIsLoading(true);
    try {
      router.post('/dashboard/ad_accounts/import', {
        accounts: selectedAccounts.map(convertAccountForSubmission)
      }, {
        onError: () => console.error('Error importing accounts'),
        onFinish: () => setIsLoading(false)
      });
    } catch (error) {
      console.error('Error importing accounts:', error);
      setIsLoading(false);
    }
  };

  const removeAccount = async (accountId: string) => {
    if (!confirm('Tem certeza que deseja remover esta conta? Todos os catálogos associados serão excluídos permanentemente.')) return;
    setLoadingById(prev => ({ ...prev, [`remove-${accountId}`]: true }));
    router.delete(`/dashboard/ad_accounts/${accountId}/remove`, {
      data: { account_ids: [accountId] },
      preserveScroll: true,
      onError: () => setLoadingById(prev => ({ ...prev, [`remove-${accountId}`]: false }))
    });
  };

  const handleScrollIndicators = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    setShowTopScrollIndicator(scrollTop > 0);
    setShowBottomScrollIndicator(scrollTop < scrollHeight - clientHeight - 5);
  };

  return (
    <>
      <Head title={page_title} />
      <div className="px-4 py-6">
        <div className="space-y-6 min-h-full">
          <BackLink label="Integrações" fallbackHref="/dashboard/integrations" />

          {/* Cabeçalho único (padrão da ficha de credencial): quadrado do ícone +
              nome + chip de status + ações. O "brilho" gradiente, o selo de
              conectado e o título central duplicado saíram. */}
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary [&_svg]:size-6">
              {PROVIDER_ICONS_SMALL.meta}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">Meta</h1>
                <ConnectionStatusChip connected={meta_connected} />
              </div>
            </div>
            {can_manage_resources && (
              <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                <a href="/dashboard/integrations/connect_facebook">
                  <Button variant="outline" size="sm">
                    {meta_connected ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" /> : <Plug className="h-3 w-3 sm:h-4 sm:w-4" />}
                    {meta_connected ? (
                      <>
                        <span className="hidden sm:inline">Reconectar</span>
                        <span className="sm:hidden">Recon.</span>
                      </>
                    ) : (
                      <span>Conectar</span>
                    )}
                  </Button>
                </a>
                {meta_connected && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-muted-foreground hover:border-destructive/40 hover:text-destructive"
                    onClick={() => {
                      if (confirm('Tem certeza que deseja desconectar o Meta/Facebook? Isso removerá todas as contas de anúncios importadas.')) {
                        router.delete('/dashboard/integrations/disconnect_facebook', { preserveScroll: true });
                      }
                    }}
                  >
                    <span className="hidden sm:inline">Desconectar</span>
                    <span className="sm:hidden">Desc.</span>
                  </Button>
                )}
              </div>
            )}
          </div>

          {!can_manage_resources && (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Você não tem permissão para gerenciar integrações.</AlertTitle>
              <AlertDescription>Você pode visualizar as integrações conectadas, mas não pode modificá-las.</AlertDescription>
            </Alert>
          )}

          <Card><CardContent className="p-6">
            <div className="space-y-4">
              {!meta_connected ? (
                <div className="space-y-4 mt-4">
                  <p className="text-[13px] text-muted-foreground">
                    Conecte sua conta do Meta para sincronizar dados de contas de anúncios.
                  </p>
                </div>
              ) : (
                <div className="space-y-6 mt-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-[15px] font-semibold text-foreground mb-1">Contas de anúncios</h3>
                      <p className="hidden text-[13px] text-muted-foreground sm:block">Gerencie suas contas conectadas e suas configurações</p>
                    </div>
                    <Button
                      onClick={() => {
                        if (!can_manage_resources) return;
                        setDialogOpen(true);
                        fetchAvailableAccounts();
                      }}
                      variant="outline"
                      size="sm"
                      disabled={!can_manage_resources}
                      className="w-full sm:w-auto"
                    >
                      <Plug className="h-4 w-4" />
                      Conectar contas
                    </Button>
                  </div>

                  {userAccounts.length > 0 && (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input type="search" className="pl-9" placeholder="Buscar contas..." value={adConnectedSearchTerm} onChange={(e) => setAdConnectedSearchTerm(e.target.value)} />
                    </div>
                  )}

                  {userAccounts.length > 0 ? (
                    <div className="space-y-3">
                      {adConnectedSearchTerm && (
                        <div className="text-sm text-muted-foreground mb-2">
                          {filteredAdAccounts.length} resultado(s) de {userAccounts.length} conta(s)
                        </div>
                      )}
                      {filteredAdAccounts.length > 0 ? (
                        <div className="relative">
                          {showTopScrollIndicator && (
                            <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-card to-transparent pointer-events-none z-10 flex items-start justify-center pt-1">
                              <ChevronUp className="h-4 w-4 text-muted-foreground animate-bounce" />
                            </div>
                          )}
                          <div
                            id="ad-accounts-scroll-container"
                            className="space-y-2 max-h-[300px] overflow-y-auto pr-2"
                            onScroll={handleScrollIndicators}
                          >
                            {filteredAdAccounts.map((account: AdAccount) => (
                              <div key={account.id} className="group">
                                <div className="relative bg-background/30 border border-border/50 rounded-lg p-4 hover:bg-background/50 hover:border-border/50 transition-all duration-200">
                                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                    <div className="flex items-start justify-between md:hidden">
                                      <div className="min-w-0 flex-1 mr-3">
                                        <h3 className="font-medium text-foreground text-sm truncate" title={account.name}>{account.name}</h3>
                                        <p className="text-muted-foreground font-mono text-xs mt-0.5">{account.external_id}</p>
                                      </div>
                                      <Badge variant={account.status === 'active' ? 'success' : 'info'} className="text-[11px] px-1.5">
                                        {account.status === 'active' ? 'Conectada' : 'Inativa'}
                                      </Badge>
                                    </div>
                                    <div className="hidden md:flex md:items-center md:flex-1 md:gap-6">
                                      <div className="min-w-0 flex-1">
                                        <h3 className="font-medium text-foreground text-sm truncate" title={account.name}>{account.name}</h3>
                                        <p className="text-muted-foreground font-mono text-xs mt-0.5">{account.external_id}</p>
                                      </div>
                                      <Badge variant={account.status === 'active' ? 'success' : 'info'} className="text-[11px] px-1.5">
                                        {account.status === 'active' ? 'Conectada' : 'Inativa'}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center justify-end gap-2 mt-3 md:mt-0">
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() => can_manage_resources && removeAccount(account.id)}
                                        disabled={loadingById[`remove-${account.id}`] || !can_manage_resources}
                                        className={!can_manage_resources ? 'opacity-50' : ''}
                                      >
                                        {loadingById[`remove-${account.id}`] ? <Loader2 className="h-4 w-4 animate-spin" /> : <MinusCircle className="h-4 w-4" />}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {showBottomScrollIndicator && (
                            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent pointer-events-none z-10 flex items-end justify-center pb-1">
                              <ChevronDown className="h-4 w-4 text-muted-foreground animate-bounce" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-muted-foreground mb-2">
                            <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          </div>
                          <h4 className="text-[15px] font-semibold text-foreground mb-2">Nenhuma conta encontrada</h4>
                          <p className="text-[13px] text-muted-foreground">Tente alterar os termos da sua busca.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="relative inline-block mb-4">
                        <div className="flex size-16 items-center justify-center rounded-full bg-secondary">
                          <Plug className="h-8 w-8 text-muted-foreground" />
                        </div>
                      </div>
                      <h4 className="text-[15px] font-semibold text-foreground mb-2">Nenhuma conta de anúncio conectada</h4>
                      <p className="text-[13px] text-muted-foreground max-w-sm mx-auto mb-4">
                        Conecte suas contas do Meta/Facebook para começar a gerenciar campanhas e analisar performance de forma integrada.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent></Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-success [&_svg]:size-6">
                  <MessageCircle />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h3 className="text-[15px] font-semibold text-foreground">WhatsApp Business</h3>
                    <ConnectionStatusChip connected={whatsapp_connected} />
                  </div>
                  <p className="text-[13px] text-muted-foreground">
                    Conecte a WABA do cliente via Meta Embedded Signup.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {whatsapp_waba ? (
                  <div className="rounded-lg border border-border/60 bg-background/40 p-4 space-y-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-medium text-foreground">{whatsapp_waba.name || 'WABA sem nome'}</p>
                        <p className="text-[11.5px] text-muted-foreground font-mono">{whatsapp_waba.external_id}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={whatsapp_waba.webhook_subscribed ? 'success' : 'info'}>
                          {whatsapp_waba.webhook_subscribed ? 'Webhook ativo' : 'Webhook pendente'}
                        </Badge>
                        <Badge variant="outline">{whatsapp_waba.phones_count} telefone(s)</Badge>
                      </div>
                    </div>
                    <p className="text-[13px] text-muted-foreground">
                      Os próximos números atribuídos aos colaboradores digitais desta organização sairão desta WABA.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border/70 p-4">
                    <p className="text-[13px] text-muted-foreground">
                      Nenhuma WABA conectada ainda. Abra o canal WhatsApp em um colaborador digital para iniciar o fluxo oficial da Meta.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <AssignedWorkersBanner
            workers={digital_workers.filter((w) => w.assigned)}
            onAddWorker={() => setAssignWorkersOpen(true)}
            onRemoveWorker={(workerId) => router.delete('/dashboard/integrations/remove_worker', { data: { provider: 'meta', worker_id: workerId }, preserveScroll: true })}
          />
        </div>
      </div>

      {/* Account Selection Modal */}
      <Dialog open={dialogOpen} onOpenChange={(v) => !v && setDialogOpen(false)}>
        <DialogContent className="max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Contas de anúncios disponíveis</DialogTitle>
            <DialogDescription>Selecione as contas do Meta que deseja conectar ao sistema</DialogDescription>
          </DialogHeader>

          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar por nome ou ID..." className="pl-9" />
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="mt-4 text-muted-foreground">Carregando contas disponíveis...</p>
            </div>
          ) : metaAccounts.length > 0 ? (
            (() => {
              const filteredAccounts = metaAccounts
                .filter(account => !account.imported)
                .filter(account => {
                  if (!searchTerm) return true;
                  const term = searchTerm.toLowerCase();
                  return account.name.toLowerCase().includes(term) || account.external_id.toLowerCase().includes(term);
                });

              if (filteredAccounts.length === 0 && searchTerm) {
                return (
                  <div className="text-center py-8">
                    <div className="mb-4 inline-flex size-16 items-center justify-center rounded-full bg-secondary">
                      <Archive className="size-8 text-muted-foreground" />
                    </div>
                    <p className="text-[15px] font-semibold text-foreground mb-2">Nenhuma conta encontrada</p>
                    <p className="text-[13px] text-muted-foreground mb-6">Nenhuma conta encontrada com o termo pesquisado.</p>
                  </div>
                );
              }

              return metaAccounts.some(account => !account.imported) ? (
                <div className="rounded-md border border-border overflow-hidden max-h-[400px] overflow-y-auto">
                  <Table className="text-[13px]">
                    <TableHeader>
                      <TableRow className="border-b border-border bg-muted">
                        <TableHead className={`w-12 ${TH_CLS}`}>{''}</TableHead>
                        <TableHead className={TH_CLS}>Nome</TableHead>
                        <TableHead className={TH_CLS}>ID</TableHead>
                        <TableHead className={TH_CLS}>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAccounts.map((account) => (
                        <TableRow
                          key={account.external_id}
                          onClick={() => toggleAccountSelection(account.external_id)}
                          className={`cursor-pointer transition-all duration-200 ease-in-out ${
                            account.selected ? 'bg-secondary hover:bg-accent' : 'hover:bg-accent/30'
                          }`}
                        >
                          <TableCell>
                            <Checkbox checked={account.selected || false} onCheckedChange={() => toggleAccountSelection(account.external_id)} />
                          </TableCell>
                          <TableCell className="font-medium text-foreground text-[13px]">{account.name}</TableCell>
                          <TableCell className="text-muted-foreground font-mono text-[13px]">{account.external_id}</TableCell>
                          <TableCell>
                            <Badge variant={account.status === 'active' ? 'success' : 'info'}>
                              <span className="inline-flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full ${account.status === 'active' ? 'bg-success' : 'bg-muted-foreground'}`} />
                                {account.status === 'active' ? 'Ativo' : account.status}
                              </span>
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mb-4 inline-flex size-16 items-center justify-center rounded-full bg-success/10">
                    <Check className="size-8 text-success" />
                  </div>
                  <p className="text-[15px] font-semibold text-foreground mb-2">Todas as contas já estão conectadas</p>
                  <p className="text-[13px] text-muted-foreground mb-6">Todas as contas de anúncios disponíveis em sua conta Meta já foram importadas.</p>
                </div>
              );
            })()
          ) : (
            <div className="text-center py-8">
              <div className="mb-4 inline-flex size-16 items-center justify-center rounded-full bg-secondary">
                <Archive className="size-8 text-muted-foreground" />
              </div>
              <p className="text-[15px] font-semibold text-foreground mb-2">Nenhuma conta disponível</p>
              <p className="text-[13px] text-muted-foreground mb-6">Não foram encontradas contas de anúncios para conectar.</p>
            </div>
          )}

          <DialogFooter>
            {metaAccounts.some(account => !account.imported) && !isLoading && metaAccounts.length > 0 ? (
              <>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button
                  onClick={() => {
                    importSelectedAccounts();
                    setDialogOpen(false);
                  }}
                  disabled={isLoading || !metaAccounts.some(account => account.selected && !account.imported)}
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isLoading ? 'Conectando...' : 'Conectar selecionadas'}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Fechar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AssignWorkersModal
        open={assignWorkersOpen}
        onClose={() => setAssignWorkersOpen(false)}
        providerLabel="Meta Ads"
        workers={digital_workers}
        provider="meta"
        saving={savingWorkerAssignment}
        onSavingChange={setSavingWorkerAssignment}
      />

      {justConnected.worker && (
        <PostConnectionModal
          open={justConnected.open}
          onClose={justConnected.close}
          worker={justConnected.worker}
          context="Meta"
        />
      )}

    </>
  );
};

MetaPage.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>;

export default MetaPage;
